import { useMemo } from 'react';
import { Link, useOutletContext, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlarmClock, ArrowRight, CheckCircle2, Wrench } from 'lucide-react';
import { canAccess, capabilitiesIn } from '../lib/access.js';
import {
  api,
  type AutomationCandidate,
  type StalledTicket,
  type StallReason,
} from '../lib/api.js';
import { workingTimeShort } from '@esa/shared';
import { num } from '../lib/format.js';
import {
  AccessDenied,
  Badge,
  EmptyState,
  ErrorState,
  PageHeader,
  Panel,
  Select,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  type Tone,
} from '../ui/index.js';

interface Ctx {
  tenant: string;
  role: string | null;
  tenantName: string | undefined;
  /** /me, for this desk's capabilities (a desk may change who sees reports). */
  me?: { tenants: { id: string; capabilities?: string[] }[] };
}

const WINDOW_OPTIONS = [
  { value: '90', label: 'Last 90 days' },
  { value: '180', label: 'Last 180 days' },
  { value: '365', label: 'Last 365 days' },
];

const REASON_LABEL: Record<StallReason, string> = {
  never_answered: 'Never answered',
  awaiting_us: 'Waiting on us',
  awaiting_requester: 'Waiting on the requester',
  awaiting_third_party: 'Waiting on a third party',
  unassigned: 'Unassigned',
  unrouted: 'Needs routing',
  moving: 'Moving',
};

// Roughly tracks the severity bands `diagnoseStall` computes (see the core's
// comment), but chosen for what a lead does next rather than for a score:
// "waiting on us" and "unassigned" are ours to fix right now, "waiting on a
// third party" needs a chase rather than desk time, and a paused clock is not
// an emergency.
const REASON_TONE: Record<StallReason, Tone> = {
  never_answered: 'danger',
  awaiting_us: 'danger',
  unassigned: 'warn',
  awaiting_third_party: 'warn',
  unrouted: 'info',
  awaiting_requester: 'neutral',
  moving: 'neutral',
};

const VERDICT_LABEL: Record<AutomationCandidate['verdict'], string> = {
  ready: 'Ready',
  candidate: 'Candidate',
  needs_work: 'Needs work',
  not_worth_it: 'Not worth it',
};

const VERDICT_TONE: Record<AutomationCandidate['verdict'], Tone> = {
  ready: 'ok',
  candidate: 'brand',
  needs_work: 'warn',
  not_worth_it: 'neutral',
};

/**
 * Daylighting (FR-RPT-12): two questions answered straight from the record,
 * with no model involved.
 *
 * That is deliberate and it shapes everything below: a queue lead told
 * "nothing has happened for 12 working days, and we answered last" can check
 * that in one click. A generated sentence saying roughly the same thing
 * cannot be checked, and the first time it is wrong about a real ticket the
 * whole panel loses its credibility. So every line here is a fact plus a next
 * action, never advice or a summary.
 *
 * The core logic and the API service both existed and were tested before this
 * page did — the report was built and then nobody could see it, which is the
 * worst of both worlds. This is the wiring.
 */
export function DaylightPage() {
  const { tenant, role, tenantName, me } = useOutletContext<Ctx>();
  // report.tenant — the same threshold as the dashboard and desk-performance
  // report. Stalls and automation cases read the same tenant data, not a
  // separate capability, and a nav item that disagreed with its page's gate
  // would open onto an AccessDenied a moment after being offered.
  const allowed = canAccess(role, 'dashboard', capabilitiesIn(me, tenant));

  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'stalls';
  const windowDays = params.get('windowDays') ?? '180';
  const setParam = (key: string, value: string) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(key, value);
        return next;
      },
      { replace: true },
    );

  const stalls = useQuery({
    queryKey: ['daylight-stalls', tenant],
    queryFn: () =>
      api.get<{ stalls: StalledTicket[] }>(
        `/api/v1/tenants/${tenant}/reports/stalls?limit=200`,
      ),
    enabled: Boolean(tenant) && allowed,
  });

  const cases = useQuery({
    queryKey: ['daylight-automation', tenant, windowDays],
    queryFn: () =>
      api.get<{ candidates: AutomationCandidate[] }>(
        `/api/v1/tenants/${tenant}/reports/automation-cases?windowDays=${windowDays}`,
      ),
    enabled: Boolean(tenant) && allowed,
  });

  if (!allowed) {
    return <AccessDenied area="dashboard" role={role} tenantName={tenantName} />;
  }

  return (
    <div className="mx-auto max-w-[1180px] p-4 sm:p-6">
      <PageHeader
        title="Stalls and automation candidates"
        description="What the record already knows, made actionable: why open work is not moving, and which repeated requests are worth automating."
      />

      <Tabs value={tab} onValueChange={(v) => setParam('tab', v)}>
        <TabsList className="mb-4">
          <TabsTrigger value="stalls" count={stalls.data?.stalls.length}>
            <AlarmClock aria-hidden className="h-3.5 w-3.5" /> Stalled tickets
          </TabsTrigger>
          <TabsTrigger value="automation" count={cases.data?.candidates.length}>
            <Wrench aria-hidden className="h-3.5 w-3.5" /> Automation cases
          </TabsTrigger>
        </TabsList>

        <TabsContent value="stalls">
          <Panel
            title="Open work that is not moving"
            subtitle="Sorted worst first. Age is working minutes on the SLA clock, not the calendar — a Friday afternoon reply does not read as three days of silence, but it also means this will not match a plain day count."
          >
            {stalls.error && <ErrorState error={stalls.error} />}
            {stalls.isLoading && <Spinner label="Loading" />}
            {stalls.data &&
              (stalls.data.stalls.length === 0 ? (
                <EmptyState
                  icon={<CheckCircle2 />}
                  title="No open work is stuck"
                  hint="Every open ticket is either recently touched, correctly paused waiting on someone, or new enough that neither applies yet."
                />
              ) : (
                <StallGroups tenant={tenant} stalls={stalls.data.stalls} />
              ))}
          </Panel>
        </TabsContent>

        <TabsContent value="automation">
          <Panel
            title="Which repeated requests are worth automating"
            subtitle={
              <>
                Ordered by volume, extrapolated to a year. Expect most verdicts to read{' '}
                <strong>needs work</strong> with handling time named as the blocker — ESA never
                filled in FreshService&rsquo;s Time Tracked column, so desk time is genuinely
                unknown for almost every request type. That is the honest state this report is
                supposed to show, not a gap in it.
              </>
            }
            action={
              <Select
                value={windowDays}
                onValueChange={(v) => setParam('windowDays', v)}
                options={WINDOW_OPTIONS}
                size="sm"
                aria-label="Reporting window"
                className="w-36"
              />
            }
          >
            {cases.error && <ErrorState error={cases.error} />}
            {cases.isLoading && <Spinner label="Loading" />}
            {cases.data &&
              (cases.data.candidates.length === 0 ? (
                <EmptyState
                  title="No repeated request type in this window"
                  hint="A request type needs at least five closed tickets in the window before a case can be made either way."
                />
              ) : (
                <ul className="space-y-3">
                  {cases.data.candidates.map((c) => (
                    <CaseRow key={c.requestType} candidate={c} />
                  ))}
                </ul>
              ))}
          </Panel>
        </TabsContent>
      </Tabs>
    </div>
  );
}

/**
 * Stalls grouped by reason. "Waiting on us" and "waiting on a third party"
 * need completely different responses from a lead, and a flat list hides
 * that distinction — so the grouping IS the finding, not just tidying.
 *
 * Groups are ordered by which reason's WORST ticket appears first in the
 * server's worst-first list (a `Map` keeps insertion order, and the first
 * ticket seen for a reason is that reason's most severe one) — so the most
 * urgent reason still leads the page, the ordering contract just moves from
 * "ticket by ticket" to "reason by reason".
 */
function StallGroups({ tenant, stalls }: { tenant: string; stalls: StalledTicket[] }) {
  const groups = useMemo(() => {
    const byReason = new Map<StallReason, StalledTicket[]>();
    for (const s of stalls) {
      const arr = byReason.get(s.reason);
      if (arr) arr.push(s);
      else byReason.set(s.reason, [s]);
    }
    return [...byReason.entries()];
  }, [stalls]);

  return (
    <div className="space-y-5">
      {groups.map(([reason, items]) => (
        <div key={reason}>
          <div className="mb-2 flex items-center gap-2">
            <Badge tone={REASON_TONE[reason]}>{REASON_LABEL[reason]}</Badge>
            <span className="text-2xs text-ink-tertiary">
              {items.length} ticket{items.length === 1 ? '' : 's'}
            </span>
          </div>
          <ul className="space-y-2">
            {items.map((s) => (
              <StallRow key={s.id} tenant={tenant} stall={s} />
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

/**
 * One stalled ticket. `finding` and `nextAction` are the content — everything
 * else is context around them. The next action is styled to lead the eye,
 * because it is the point of the screen: a lead who reads only that line
 * still knows what to do.
 */
function StallRow({ tenant, stall }: { tenant: string; stall: StalledTicket }) {
  return (
    <li className="rounded-md border border-line-subtle bg-surface p-3">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        {/* PAINTED AS A LINK, and covering the subject too.
            This was already a Link, but in `text-ink` -- indistinguishable
            from the subject beside it until hovered, so on a list of 159 the
            way through to the ticket was undiscoverable. The subject is
            inside the target because that is the phrase a reader aims at;
            the number alone is a small target for the thing you most want
            to do from this pane. */}
        <Link
          to={`/t/${tenant}/${stall.number}`}
          className="group flex min-w-0 flex-1 items-center gap-2 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-line"
        >
          <span className="nums shrink-0 font-medium text-brand group-hover:underline">
            #{stall.number}
          </span>
          <span className="min-w-0 flex-1 truncate text-sm text-ink-secondary group-hover:text-ink">
            {stall.subject}
          </span>
        </Link>
        {/* WORKING time, in working units. `minutes()` renders with a
            24-hour day, which turned 23.8 working days into "8.9d" — beside
            a finding that said "12 working days", on the same row. A reader
            who noticed could not tell which half was wrong. */}
        <Badge tone="neutral">{workingTimeShort(stall.ageBusinessMinutes)} open</Badge>
      </div>
      <p className="mt-1.5 text-sm text-ink">{stall.finding}</p>
      <p className="mt-1 flex items-start gap-1.5 text-sm font-medium text-brand">
        <ArrowRight aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        {stall.nextAction}
      </p>
      <p className="mt-1.5 text-2xs text-ink-tertiary">
        {stall.queue ?? 'No queue'} · {stall.assignee ?? 'Unassigned'}
        {stall.requestType ? ` · ${stall.requestType}` : ''}
      </p>
    </li>
  );
}

/**
 * One automation case. The assumptions are rendered open by default in a
 * native `<details>` — a number whose assumptions cannot be seen cannot be
 * argued with, and one that cannot be argued with does not get acted on. It
 * stays collapsible so a reader who has already checked them once can get it
 * out of the way, but nothing here is hidden behind a tooltip.
 */
function CaseRow({ candidate: c }: { candidate: AutomationCandidate }) {
  return (
    <li className="rounded-md border border-line-subtle bg-surface p-3.5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-ink">{c.requestType}</p>
          <p className="mt-0.5 text-2xs text-ink-tertiary">
            {num(c.perYear)}/year · {num(c.tickets)} tickets from {num(c.requesters)} requesters in
            window · {c.ticketsPerRequester.toFixed(1)} per requester
          </p>
        </div>
        <Badge tone={VERDICT_TONE[c.verdict]}>{VERDICT_LABEL[c.verdict]}</Badge>
      </div>

      <p className="mt-2 text-sm text-ink">{c.rationale}</p>

      {/* `hoursPerYear === null` means "volume known, handling time unknown"
          and must never read as zero or as a bare dash. Substituting elapsed
          time for missing desk time once produced "20,199 working hours a
          year" on one request type — nine full-time people — which is the
          kind of number that discredits every other number on the page. */}
      <p className="mt-1 text-xs text-ink-secondary">
        {c.hoursPerYear === null
          ? 'Handling time: unknown. Volume is known, but no desk time is recorded for this type, so a saving cannot be sized — that gap is itself a finding.'
          : `About ${num(c.hoursPerYear)} working hours a year.`}
      </p>

      {c.blockers.length > 0 && (
        <div className="mt-2">
          <p className="text-2xs font-semibold uppercase tracking-wide text-ink-tertiary">
            What would have to change
          </p>
          <ul className="mt-1 space-y-0.5 text-xs text-ink-secondary">
            {c.blockers.map((b, i) => (
              <li key={i} className="flex gap-1.5">
                <span aria-hidden className="text-ink-tertiary">
                  &bull;
                </span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <details className="mt-2" open>
        <summary className="cursor-pointer select-none text-2xs font-medium text-ink-tertiary hover:text-ink-secondary">
          Assumptions ({c.assumptions.length})
        </summary>
        <ul className="mt-1.5 space-y-0.5 border-l-2 border-line-subtle pl-2.5 text-xs text-ink-secondary">
          {c.assumptions.map((a, i) => (
            <li key={i}>{a}</li>
          ))}
        </ul>
      </details>
    </li>
  );
}
